/**
 * @fileoverview Pure helper deriving the left-pane activation list from the
 *             selected columns + the currently selected field.
 * @module ui-admin/components/ReportCanvasBuilder/columnActivationList
 *
 * Responsibilities: - Map ReportColumn[] to the activation-list row shape the
 *                     left pane renders (field, label, active flag, index).
 * Dependencies:     - @firesquirrel/local-db (ReportColumn type only).
 * Architecture:     - Pure function; no side effects.
 * Notes:            - The activation list controls column visibility; the
 *                     canvas renders the same list order as the report table.
 */

import type { ReportColumn } from '@firesquirrel/local-db';

/**
 * @contract ColumnActivationEntry
 * Purpose: One row in the activation checklist.
 * Fields:  field, label, active (selected), index (position in columns).
 */
export interface ColumnActivationEntry {
  field: string;
  label: string;
  active: boolean;
  index: number;
}

/**
 * @reusable columnActivationList
 * Purpose: Derive the activation list (ordered columns with active highlight)
 *   from the columns array + the currently selected field.
 * Rules:   Pure function; preserves column order; no side effects.
 */
export function columnActivationList(
  columns: ReportColumn[],
  selectedField: string | null,
): ColumnActivationEntry[] {
  return columns.map((col, index) => ({
    field: col.field,
    label: col.label,
    active: col.field === selectedField,
    index,
  }));
}
